import React, { useEffect, useState, memo } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as actionTypes from '@redux/actionTypes';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@context/index';
import ShareDiscountCouponModal from '@components/Modal/ShareDiscountCouponModal';
import { safeLocalStorage } from '@utils/localstorage';
import { setAppMode, APP_MODES } from '@redux/slices/appModeSlice';

// material-ui
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';

// images
import SaveingIcon from '@images/owner/savings.svg';

import { getSidenavItems } from './sidenavItems';
const isBrowser = typeof window !== 'undefined';

const normalizePath = (path) => (path || '/').split('?')[0].replace(/\/$/, '') || '/';

const OwnerSidebarNav = () => {
	const router = useRouter();
	const [pendingPath, setPendingPath] = useState(null);

	// Highlight the clicked item immediately; router.asPath updates after the route finishes.
	useEffect(() => {
		const onStart = (url) => setPendingPath(normalizePath(url));
		const clearPending = () => setPendingPath(null);

		router.events.on('routeChangeStart', onStart);
		router.events.on('routeChangeComplete', clearPending);
		router.events.on('routeChangeError', clearPending);

		return () => {
			router.events.off('routeChangeStart', onStart);
			router.events.off('routeChangeComplete', clearPending);
			router.events.off('routeChangeError', clearPending);
		};
	}, [router.events]);

	const currentPath = normalizePath(router.asPath);
	const activePath = pendingPath ?? currentPath;
	const matchesPath = (itemPath) => activePath === normalizePath(itemPath);
	const dispatch = useDispatch();
	const { web3AuthLogout } = useAuth();
	const userDetails = useSelector((state) => state?.setUserDetails?.user);
	const appMode = useSelector((state) => state?.appMode?.mode) || APP_MODES.EARTH_ROVERS;
	const sidenavItems = getSidenavItems(appMode);

	const handleModeSwitch = (mode) => {
		dispatch(setAppMode(mode));
		if (currentPath !== '/owner/dashboard') {
			router.push('/owner/dashboard');
		}
	};

	const handleSignOut = () => {
		try {
			if (isBrowser) {
				if (window?.Intercom) {
					window.Intercom('shutdown');
				}
				if (window?.gtag) {
					window.gtag('config', 'G-XXFJFZBQM2', {
						user_id: userDetails?.id
					});
					window.gtag('event', 'sign_out', {
						user_id: userDetails?.id
					});
				}
				safeLocalStorage.clear();
				safeLocalStorage.clearSessionStorage();
				safeLocalStorage.setItem('openlogin_store', {});
				web3AuthLogout();
				dispatch({
					type: actionTypes.CLEAR_REDUX,
					payload: {}
				});
				window.location.href = '/dashboard-login';
				// TODO: @franciscomasi To use Navigator we need to clean Redux properly
				// navigate('/dashboard-login');
			}
		} catch (e) {
			console.log('error:', e);
		}
	};

	const privacyPolicyHandler = () => {
		window.open('/privacy-policy', '_blank');
	};

	const termsconditionsHandler = () => {
		window.open('/terms', '_blank');
	};

	const shareCodeHandler = () => {
		NiceModal.show(ShareDiscountCouponModal);
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				height: '100%',
				padding: '10px'
			}}
		>
			<Box>
				{userDetails?.robocaps_enabled && (
					<Box className="flex h-[64px] p-[8px] items-center">
						<Box className="flex bg-white rounded-[1000px] p-[2px]">
							<button
								className={`px-[12px] py-[6px] rounded-[1000px] text-[14px] font-semibold tracking-[-0.56px] transition-all ${
									appMode === APP_MODES.EARTH_ROVERS
										? 'bg-[#000] text-[#F3F3F3]'
										: 'bg-transparent text-[#727272] hover:text-[#000]'
								}`}
								onClick={() => handleModeSwitch(APP_MODES.EARTH_ROVERS)}
							>
								Earth Rovers
							</button>
							<button
								className={`px-[12px] py-[6px] rounded-[1000px] text-[14px] font-semibold tracking-[-0.56px] transition-all ${
									appMode === APP_MODES.ROBOCAPS
										? 'bg-[#000] text-[#F3F3F3]'
										: 'bg-transparent text-[#727272] hover:text-[#000]'
								}`}
								onClick={() => handleModeSwitch(APP_MODES.ROBOCAPS)}
							>
								RoboCaps
							</button>
						</Box>
					</Box>
				)}
				{userDetails?.robocaps_enabled && <Divider />}
				<List className="flex py-[8px] flex-col items-start gap-[8px]">
					{sidenavItems.map((item, index) => (
						<li
							key={index}
							className={`w-full flex p-[8px] items-center gap-[8px] rounded-[8px] ${matchesPath(item.path) ? 'bg-[#C6C6C6]' : 'bg-transparent'}`}
						>
							{item.link ? (
								<a
									className="w-full flex px-[8px] py-0 flex-col items-start gap-[8px] rounded-[8px]"
									href={item.link}
									target="_blank"
									rel="noopener noreferrer"
								>
									<div className="w-full flex justify-between items-center">
										<div className="flex flex-row items-center gap-[8px]">
											{typeof item.icon === 'function' ? (
												matchesPath(item.path) ? (
													<item.selectIcon />
												) : (
													<item.icon />
												)
											) : (
												<img
													src={matchesPath(item.path) ? item.selectIcon : item.icon}
													alt={item.name}
													className="w-[24px] h-[24px]"
												/>
											)}
											<p
												className={`${
													matchesPath(item.path) ? 'text-[#000]' : 'text-[#727272]'
												} text-center text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]`}
											>
												{item.name}
											</p>
										</div>
										<div>{matchesPath(item.path) ? item.selectTab : item.tab}</div>
									</div>
								</a>
							) : (
								<Link
									className={`w-full flex px-[8px] py-0 flex-col items-start gap-[8px] rounded-[8px]`}
									href={item.path}
									prefetch
								>
									<div className="w-full flex justify-between items-center">
										<div className="flex flex-row items-center gap-[8px]">
											{typeof item.icon === 'function' ? (
												matchesPath(item.path) ? (
													<item.selectIcon />
												) : (
													<item.icon />
												)
											) : (
												<img
													src={matchesPath(item.path) ? item.selectIcon : item.icon}
													alt={item.name}
													className="w-[24px] h-[24px]"
												/>
											)}
											<p
												className={`${
													matchesPath(item.path) ? 'text-[#000]' : 'text-[#727272]'
												} text-center text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]`}
											>
												{item.name}
											</p>
										</div>
									</div>
								</Link>
							)}
						</li>
					))}
				</List>
			</Box>
			<Box className="flex p-[6px] flex-col items-start">
				{userDetails?.referral_code && (
					<Box className="w-full pb-[24px] flex flex-col items-center gap-[16px] border-b-[1px] border-b-solid border-b-#DDD">
						<div className="w-full flex flex-col justify-center items-center gap-[16px] rounded-[8px]">
							<div className="flex flex-col justify-center items-center gap-[8px]">
								<img
									src={SaveingIcon}
									alt="saveing-icon"
								/>
								<div className="flex flex-col justify-center items-center gap-[4px]">
									<p className="text-[#000] text-center text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]">
										Refer a friend, get 250 FBP!
									</p>
									<p className="text-[#000] text-center text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]">
										(Limited Time Only)
									</p>
								</div>
							</div>
							<p className="w-[90%] text-[#727272] text-center text-[16px] not-italic font-medium leading-[22px] tracking-[-0.32px]">
								Share your code with a friend! They'll get a $50 discount for an EarthRover and you'll get 250FBP!
							</p>
						</div>
						<button
							className="w-full flex h-[56px] px-[16px] py-[12px] justify-center items-center gap-[10px] rounded-[39px] border-[1px] border-[solid] border-[#25BD66] bg-[#008735]"
							onClick={shareCodeHandler}
						>
							<p className="text-[#FFF] text-[20px] not-italic font-semibold leading-[20px] tracking-[-0.8px]">
								Share code
							</p>
						</button>
					</Box>
				)}
				<Box className="pt-[24px] flex flex-col items-start gap-[8px]">
					<button
						className="flex h-[32px] items-center gap-[10px]"
						onClick={termsconditionsHandler}
					>
						<p className="text-[#727272] text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]">
							Terms and Conditions
						</p>
					</button>
					<button
						className="flex h-[32px] items-center gap-[10px]"
						onClick={privacyPolicyHandler}
					>
						<p className="text-[#727272] text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]">
							Privacy Policy
						</p>
					</button>
					<button
						className="flex h-[32px] items-center gap-[10px]"
						onClick={handleSignOut}
					>
						<p className="text-[#FF1053] text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]">
							Sign Out
						</p>
					</button>
				</Box>
			</Box>
		</Box>
	);
};

export default memo(OwnerSidebarNav);
